(() => {
  if (customElements.get('fct-sticker-picker')) return;

  class FctStickerPicker extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.picksResizeObserver?.disconnect();
      cancelAnimationFrame(this.picksHeightFrame);
      this.picksHeightFrame = null;
      this.observedPickNodes = new Set();
      this.controller = new AbortController();
      this.picksResizeObserver = typeof ResizeObserver === 'function' ? new ResizeObserver(() => this.schedulePicksHeight()) : null;
      this.products = new Map();
      this.variants = new Map();
      this.draft = new Map();
      this.selectedVariants = new Map();
      this.cart = null;
      this.busy = false;
      this.switchingMode = false;
      this.catalogComplete = false;
      this.readSequence = 0;
      this.config = this.readJSON(this.querySelector('[data-picker-config]'), {});
      this.config.strings ||= {};
      this.config.mode ||= 'mini';
      this.config.discountVerified = this.config.offer?.enabled === true;
      this.offerPreview = !this.config.discountVerified && this.config.offer?.preview === true;
      this.maxPicks = Math.max(3, Math.min(15, Number(this.config.maxPicks) || 15));
      this.config.tiers = this.config.offer?.tiers || this.config.tiers;
      this.eligibleVariantIds = new Set((this.config.offer?.variantIds || []).map(String));
      this.tiers = (Array.isArray(this.config.tiers) ? this.config.tiers : [])
        .filter(tier => Number.isInteger(Number(tier.quantity)) && Number(tier.quantity) > 0)
        .map(tier => ({ quantity: Number(tier.quantity), freeQuantity: Math.max(0, Math.min(Number(tier.quantity) - 1, Math.floor(Number(tier.freeQuantity) || 0))), discountQuantity: Math.max(0, Number(tier.discountQuantity) || 0), discountPercentage: Math.max(0, Math.min(100, Number(tier.discountPercentage) || 0)), code: String(tier.code || ''), percentage: Math.max(0, Math.min(100, Number(tier.percentage) || 0)), total: Number(tier.total) || 0 }))
        .sort((a, b) => a.quantity - b.quantity);
      this.storageKey = ['fct-picker-v1', location.hostname, this.config.catalogUrl || this.config.sectionId || location.pathname, this.config.currency || 'USD', this.config.ruleVersion || '1'].join(':');
      this.addProducts(this.readJSON(this.querySelector('[data-picker-products]'), []));
      this.restoreDraft();
      this.mount = this.querySelector('[data-picker-summary-mount]');
      this.summary = this.querySelector('[data-picker-summary]');
      this.dialog = this.querySelector('[data-picker-dialog]');
      this.detailDialog = this.querySelector('[data-picker-detail]');
      this.detailProductId = null;
      this.openVariantCard = null;
      this.mobileQuery = matchMedia('(max-width: 989px)');
      this.bindEvents();
      this.render();
      this.refreshCart().then(() => this.recoverPending()).catch(() => {
        this.setStatus(this.string('cartUnavailable', 'Your picks are saved. Cart details are temporarily unavailable.'));
      });
      this.loadCatalog();
      const requestedMode = new URL(location.href).searchParams.get('picker');
      if (['mini', 'bumper'].includes(requestedMode) && requestedMode !== this.config.mode) {
        this.switchMode(requestedMode);
      }
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.picksResizeObserver?.disconnect();
      this.observedPickNodes?.clear();
      cancelAnimationFrame(this.picksHeightFrame);
      this.picksHeightFrame = null;
      this.clearConfetti();
      clearTimeout(this.cartTimer);
      this.closeSummary();
      this.closeDetail();
      this.closeVariantMenu();
      this.readSequence += 1;
    }

    readJSON(node, fallback) {
      try { return node ? JSON.parse(node.textContent) : fallback; } catch { return fallback; }
    }

    string(key, fallback, values = {}) {
      let text = this.config?.strings?.[key] || fallback;
      Object.entries(values).forEach(([name, value]) => { text = text.replaceAll(`{${name}}`, String(value)); });
      return text;
    }

    money(value) {
      try {
        return new Intl.NumberFormat(this.config.locale || document.documentElement.lang || 'en-US', {
          style: 'currency', currency: this.config.currency || this.cart?.currency || 'USD'
        }).format((Number(value) || 0) / 100);
      } catch { return `${((Number(value) || 0) / 100).toFixed(2)} ${this.config.currency || 'USD'}`; }
    }

    safeURL(value) {
      try {
        const url = new URL(value, location.origin);
        return ['http:', 'https:'].includes(url.protocol) ? url.href : '';
      } catch { return ''; }
    }

    addProducts(products) {
      if (!Array.isArray(products)) return;
      for (const raw of products) {
        if (!raw || !raw.id || raw.requiresChoices || raw.requiresSellingPlan) continue;
        const product = {
          id: String(raw.id), title: String(raw.title || ''), url: this.safeURL(raw.url),
          image: this.safeURL(raw.image), category: String(raw.category || ''), variants: []
        };
        for (const variant of Array.isArray(raw.variants) ? raw.variants : []) {
          const id = String(variant.id || '');
          const price = Number(variant.price);
          if (this.eligibleVariantIds?.size && !this.eligibleVariantIds.has(id)) continue;
          if (!/^\d+$/.test(id) || !Number.isSafeInteger(price) || price < 0) continue;
          const entry = { id, title: String(variant.title || ''), price, available: variant.available === true,
            image: this.safeURL(variant.image) || product.image, product };
          product.variants.push(entry);
          this.variants.set(id, entry);
        }
        if (product.variants.length) this.products.set(product.id, product);
      }
    }

    bindEvents() {
      const signal = this.controller.signal;
      this.addEventListener('click', event => {
        const cardLink = event.target.closest('[data-picker-card] a');
        if (cardLink && this.contains(cardLink)) {
          event.preventDefault();
          event.stopPropagation();
          if (!this.busy && !this.switchingMode) this.openDetail(cardLink.closest('[data-picker-card]')?.dataset.productId, cardLink);
          return;
        }
        const control = event.target.closest('[data-picker-mode-choice], [data-picker-add], [data-picker-minus], [data-picker-plus], [data-picker-remove], [data-picker-clear], [data-picker-submit], [data-picker-mobile-open], [data-picker-close], [data-picker-recheck], [data-picker-load-more], [data-picker-detail-close], [data-picker-detail-add], [data-picker-variant-choice]');
        if (!control || !this.contains(control)) return;
        event.preventDefault();
        event.stopPropagation();
        if (control.disabled) return;
        if (control.hasAttribute('data-picker-mode-choice')) return this.switchMode(control.dataset.pickerModeChoice);
        if (this.switchingMode) return;
        if (control.hasAttribute('data-picker-mobile-open')) return this.openSummary();
        if (control.hasAttribute('data-picker-close')) return this.closeSummary();
        if (control.hasAttribute('data-picker-detail-close')) return this.closeDetail();
        if (control.hasAttribute('data-picker-recheck')) return this.recheckCart();
        if (control.hasAttribute('data-picker-load-more')) return this.loadCatalog();
        if (control.hasAttribute('data-picker-submit')) return this.submit();
        if (this.busy || this.pending) return;
        if (control.hasAttribute('data-picker-variant-choice')) {
          const card = control.closest('[data-picker-card]');
          const id = control.dataset.variantId;
          const previousQuantity = this.draft.get(id) || 0;
          this.selectedVariants.set(String(card?.dataset.productId), id);
          this.changeQuantity(id, 1);
          if ((this.draft.get(id) || 0) > previousQuantity) this.closeVariantMenu(true);
          return;
        }
        if (control.hasAttribute('data-picker-detail-add')) {
          const id = this.detailDialog.querySelector('[data-picker-detail-variant]')?.value;
          const previousQuantity = this.draft.get(id) || 0;
          this.changeQuantity(id, 1);
          this.updateDetail();
          if ((this.draft.get(id) || 0) > previousQuantity) this.closeDetail();
          return;
        }
        if (control.hasAttribute('data-picker-clear')) {
          this.draft.clear();
          this.persistDraft();
          this.render();
          return;
        }
        if (control.hasAttribute('data-picker-add')) {
          const card = control.closest('[data-picker-card]');
          const product = this.products.get(String(card?.dataset.productId));
          if (product?.variants.length > 1) {
            this.toggleVariantMenu(card);
            return;
          }
          const id = card?.querySelector('[data-picker-variant]')?.value || this.firstVariant(product)?.id;
          this.changeQuantity(id, 1);
        } else {
          const id = control.dataset.variantId;
          this.changeQuantity(id, control.hasAttribute('data-picker-remove') ? -(this.draft.get(id) || 0) : control.hasAttribute('data-picker-minus') ? -1 : 1);
        }
      }, { signal });
      this.addEventListener('input', event => {
        if (event.target.matches('[data-picker-search]')) this.renderCatalog();
      }, { signal });
      this.addEventListener('change', event => {
        if (event.target.matches('[data-picker-sort]')) this.renderCatalog();
        if (event.target.matches('[data-picker-detail-variant]')) {
          this.selectedVariants.set(this.detailProductId, event.target.value);
          this.updateDetail();
          this.updateSelectionMarkers();
        }
        if (event.target.matches('[data-picker-variant]')) {
          const card = event.target.closest('[data-picker-card]');
          this.selectedVariants.set(String(card.dataset.productId), event.target.value);
          this.updateCard(card, this.products.get(String(card.dataset.productId)));
        }
      }, { signal });
      this.addEventListener('submit', event => {
        event.preventDefault();
        event.stopPropagation();
      }, { signal });
      const refresh = () => {
        if (this.busy) return;
        clearTimeout(this.cartTimer);
        this.cartTimer = setTimeout(() => this.refreshCart().then(() => this.recoverPending()).catch(() => {}), 350);
      };
      document.addEventListener('cart:updated', refresh, { signal });
      document.addEventListener('theme:cart:add', refresh, { signal });
      window.addEventListener('pageshow', refresh, { signal });
      window.addEventListener('focus', refresh, { signal });
      document.addEventListener('visibilitychange', () => { if (!document.hidden) refresh(); }, { signal });
      document.addEventListener('pointerdown', event => {
        if (this.openVariantCard && !this.openVariantCard.contains(event.target)) this.closeVariantMenu();
      }, { capture: true, signal });
      document.addEventListener('keydown', event => {
        if (!this.openVariantCard) return;
        if (event.key === 'Escape') {
          event.preventDefault();
          this.closeVariantMenu(true);
          return;
        }
        if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
        const choices = Array.from(this.openVariantCard.querySelectorAll('[data-picker-variant-choice]')).filter(choice => !choice.disabled);
        if (!choices.length) return;
        event.preventDefault();
        const index = choices.indexOf(document.activeElement);
        const next = event.key === 'Home' ? 0 : event.key === 'End' ? choices.length - 1 : event.key === 'ArrowDown' ? (index + 1) % choices.length : (index - 1 + choices.length) % choices.length;
        choices[next].focus({ preventScroll: true });
      }, { signal });
      document.addEventListener('scroll', event => {
        const panel = this.openVariantCard?.querySelector('[data-picker-variant-panel]');
        if (panel && !panel.contains(event.target)) this.closeVariantMenu();
      }, { capture: true, passive: true, signal });
      window.addEventListener('resize', () => { this.closeVariantMenu(); this.schedulePicksHeight(); }, { passive: true, signal });
      window.addEventListener('scroll', () => this.schedulePicksHeight(), { passive: true, signal });
      window.visualViewport?.addEventListener('resize', () => this.schedulePicksHeight(), { passive: true, signal });
      window.visualViewport?.addEventListener('scroll', () => this.schedulePicksHeight(), { passive: true, signal });
      this.mobileQuery.addEventListener('change', () => { if (!this.mobileQuery.matches) this.closeSummary(); }, { signal });
      this.dialog?.addEventListener('close', () => this.restoreSummary(), { signal });
      this.dialog?.addEventListener('click', event => {
        if (event.target === this.dialog) {
          const box = this.dialog.getBoundingClientRect();
          if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) this.closeSummary();
        }
      }, { signal });
      if (this.detailDialog) this.bindDetailDialog(this.detailDialog);
    }

    firstVariant(product) {
      return product?.variants.find(variant => variant.available) || product?.variants[0];
    }

    renderModeButtons() {
      for (const button of this.querySelectorAll('[data-picker-mode-choice]')) {
        button.disabled = this.busy || Boolean(this.pending) || this.switchingMode;
        button.setAttribute('aria-pressed', String(button.dataset.pickerModeChoice === this.config.mode));
        button.setAttribute('aria-busy', String(this.switchingMode));
      }
    }

    async switchMode(mode) {
      if (!['mini', 'bumper'].includes(mode) || mode === this.config.mode || this.busy || this.pending || this.switchingMode) return;
      const catalog = this.config.catalogs?.[mode];
      const catalogURL = typeof catalog === 'string' ? catalog : catalog?.url;
      let url;
      try {
        url = new URL(catalogURL, location.origin);
        if (!catalogURL || url.origin !== location.origin || !['http:', 'https:'].includes(url.protocol)) throw new Error('Invalid catalog URL');
        url.searchParams.set('section_id', this.config.sectionId);
      } catch {
        this.setError(this.string('modeError', 'We could not load this sticker collection. Your picks are saved. Try again.'));
        return;
      }
      this.persistDraft();
      this.closeSummary();
      this.closeDetail();
      this.closeVariantMenu();
      this.switchingMode = true;
      this.renderModeButtons();
      this.setError('');
      this.setStatus(this.string('modeLoading', 'Loading your sticker collection…'));
      const controller = new AbortController();
      const abort = () => controller.abort();
      this.controller.signal.addEventListener('abort', abort, { once: true });
      const timeout = setTimeout(abort, 20000);
      try {
        const response = await fetch(url.href, { credentials: 'same-origin', headers: { Accept: 'text/html' }, signal: controller.signal });
        if (!response.ok) throw new Error('Collection request failed');
        const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
        const next = parsed.querySelector('fct-sticker-picker');
        const nextConfig = this.readJSON(next?.querySelector('[data-picker-config]'), {});
        if (!next || nextConfig.mode !== mode || !Array.isArray(this.readJSON(next.querySelector('[data-picker-products]'), null))) throw new Error('Invalid collection section');
        if (controller.signal.aborted) return;
        const replacement = document.importNode(next, true);
        const pageURL = new URL(location.href);
        pageURL.searchParams.set('picker', mode);
        history.replaceState(history.state, '', pageURL.href);
        this.replaceWith(replacement);
        replacement.querySelector(`[data-picker-mode-choice="${mode}"]`)?.focus({ preventScroll: true });
      } catch {
        if (!this.controller.signal.aborted) {
          this.setStatus('');
          this.setError(this.string('modeError', 'We could not load this sticker collection. Your picks are saved. Try again.'));
        }
      } finally {
        clearTimeout(timeout);
        this.controller.signal.removeEventListener('abort', abort);
        this.switchingMode = false;
        this.renderModeButtons();
      }
    }

    totalPicks() { return Array.from(this.draft.values()).reduce((sum, quantity) => sum + quantity, 0); }

    changeQuantity(id, delta) {
      id = String(id || '');
      const variant = this.variants.get(id);
      if (!variant || !variant.available || this.busy || this.pending) return;
      const quantity = Math.max(0, Math.min(99, (this.draft.get(id) || 0) + delta));
      if (this.totalPicks() - (this.draft.get(id) || 0) + quantity > this.maxPicks || (!this.draft.has(id) && this.draft.size >= 50)) {
        this.setStatus(this.string('limitReached', 'You can choose up to {count} stickers at a time.', { count: this.maxPicks }));
        return;
      }
      const previousCount = this.totalPicks();
      quantity ? this.draft.set(id, quantity) : this.draft.delete(id);
      this.persistDraft();
      this.setError('');
      this.renderSummary();
      this.updateSelectionMarkers();
      this.updateDetail();
      this.setStatus(this.string('selectionUpdated', '{count} picks selected.', { count: this.totalPicks() }));
      const count = this.totalPicks();
      const reached = this.tiers.filter(tier => previousCount < tier.quantity && count >= tier.quantity).at(-1);
      if (reached) this.celebrateTier(reached.quantity);
    }

    clearConfetti() {
      clearTimeout(this.confettiTimer);
      this.confettiLayer?.getAnimations({ subtree: true }).forEach(animation => animation.cancel());
      this.confettiLayer?.remove();
      this.confettiLayer = null;
    }

    celebrateTier(quantity) {
      this.clearConfetti();
      if (matchMedia('(prefers-reduced-motion: reduce)').matches || !Element.prototype.animate) return;
      const milestone = this.querySelector(`[data-picker-tier-quantity="${quantity}"]`);
      const rect = milestone?.getBoundingClientRect();
      const width = document.documentElement.clientWidth;
      const height = window.innerHeight;
      const visible = rect?.width && rect?.height && rect.bottom > 0 && rect.top < height;
      const x = visible ? Math.max(30, Math.min(width - 30, rect.left + rect.width / 2)) : width / 2;
      const y = visible ? Math.max(80, Math.min(height - 80, rect.top + rect.height / 2)) : height * 0.62;
      const layer = document.createElement('div');
      layer.className = 'fct-picker__confetti';
      layer.dataset.tier = String(quantity);
      layer.setAttribute('aria-hidden', 'true');
      layer.style.cssText = 'position:fixed;inset:0;pointer-events:none;overflow:hidden;z-index:2147483600;';
      const palette = ['#ee4e91', '#ff95c0', '#ffd35c', '#fff4df', '#7bd9c5'];
      const pieces = quantity === this.tiers.at(-1)?.quantity ? 80 : 56;
      const fragment = document.createDocumentFragment();
      for (let index = 0; index < pieces; index += 1) {
        const piece = document.createElement('span');
        const size = 5 + Math.random() * 5;
        piece.style.cssText = `position:absolute;left:${x}px;top:${y}px;width:${size}px;height:${size * 1.6}px;background:${palette[index % palette.length]};border-radius:${index % 3 === 0 ? '50%' : '2px'};`;
        fragment.append(piece);
      }
      layer.append(fragment);
      (this.dialog?.open ? this.dialog : this.detailDialog?.open ? this.detailDialog : document.body).append(layer);
      this.confettiLayer = layer;
      for (const piece of layer.children) {
        const angle = Math.PI * (1.12 + Math.random() * 0.76);
        const speed = 160 + Math.random() * 300;
        const dx = Math.cos(angle) * speed;
        const dy = Math.sin(angle) * speed;
        const rotation = (Math.random() - 0.5) * 1100;
        const frames = Array.from({ length: 13 }, (_, index) => {
          const t = index / 12;
          return { offset: t, transform: `translate3d(${dx * t}px, ${dy * t + 480 * t * t}px, 0) rotate(${rotation * t}deg) rotateY(${720 * t}deg)`, opacity: t < 0.7 ? 1 : (1 - t) / 0.3 };
        });
        piece.animate(frames, { duration: 1400 + Math.random() * 400, easing: 'linear', fill: 'both' });
      }
      this.confettiTimer = setTimeout(() => this.clearConfetti(), 1850);
    }

    restoreDraft() {
      try {
        const raw = localStorage.getItem(this.storageKey);
        if (!raw || raw.length > 32768) return;
        const saved = JSON.parse(raw);
        if (saved.version !== 1 || saved.currency !== (this.config.currency || 'USD') || !Number.isFinite(saved.updatedAt) || Date.now() - saved.updatedAt > 86400000 || saved.updatedAt > Date.now() + 60000) {
          localStorage.removeItem(this.storageKey);
          return;
        }
        let count = 0;
        for (const entry of (Array.isArray(saved.picks) ? saved.picks : []).slice(0, 50)) {
          const id = String(entry.id || '');
          const quantity = Number(entry.quantity);
          if (!/^\d+$/.test(id) || !Number.isInteger(quantity) || quantity < 1 || quantity > 99) continue;
          const retained = Math.min(quantity, this.maxPicks - count);
          if (retained < 1) break;
          this.draft.set(id, retained);
          count += retained;
        }
        if (saved.pending?.baseline && Array.isArray(saved.pending.requested) && saved.pending.requested.length <= 50) this.pending = saved.pending;
      } catch {}
    }

    persistDraft() {
      try {
        if (!this.draft.size && !this.pending) return localStorage.removeItem(this.storageKey);
        localStorage.setItem(this.storageKey, JSON.stringify({ version: 1, currency: this.config.currency || 'USD', updatedAt: Date.now(),
          picks: Array.from(this.draft, ([id, quantity]) => ({ id, quantity })), pending: this.pending || null }));
      } catch {}
    }

    validateDraft() {
      let removed = false;
      for (const id of this.draft.keys()) {
        const variant = this.variants.get(id);
        if ((this.catalogComplete && !variant) || (variant && !variant.available)) {
          this.draft.delete(id);
          removed = true;
        }
      }
      if (removed) {
        this.persistDraft();
        this.setStatus(this.string('restoredUnavailable', 'Some saved picks are no longer available and were removed.'));
      }
    }

    async request(url, options = {}) {
      const controller = new AbortController();
      const abort = () => controller.abort();
      this.controller.signal.addEventListener('abort', abort, { once: true });
      const timer = setTimeout(abort, 20000);
      try {
        const response = await fetch(url, { credentials: 'same-origin', ...options, signal: controller.signal });
        const data = await response.json();
        if (!response.ok || data.status >= 400) {
          const error = new Error(data.description || data.message || this.string('requestFailed', 'We could not complete that request.'));
          error.status = response.status;
          throw error;
        }
        return data;
      } finally {
        clearTimeout(timer);
        this.controller.signal.removeEventListener('abort', abort);
      }
    }

    cartURL(endpoint) {
      const root = window.Shopify?.routes?.root || window.theme?.routes?.root || '/';
      return `${root.replace(/\/?$/, '/')}cart${endpoint}`;
    }

    async refreshCart() {
      const sequence = ++this.readSequence;
      const cart = await this.request(this.cartURL('.js'));
      if (!Array.isArray(cart.items)) throw new Error('Invalid cart response');
      if (sequence === this.readSequence && !this.controller.signal.aborted) {
        this.cart = cart;
        this.syncCartCount(cart);
        this.renderSummary();
      }
      return cart;
    }

    syncCartCount(cart) {
      const count = Number(cart.item_count);
      if (!Number.isSafeInteger(count) || count < 0) return;
      document.querySelectorAll('[data-fct-cart-count]').forEach(badge => { badge.textContent = String(count); });
    }

    cartQuantities(cart, requestId = null) {
      const quantities = Object.create(null);
      for (const item of cart?.items || []) {
        if (requestId && item.properties?._fct_picker_request !== requestId) continue;
        const id = String(item.variant_id || item.id || '');
        quantities[id] = (quantities[id] || 0) + Math.max(0, Number(item.quantity) || 0);
      }
      return quantities;
    }

    reconcile(requested, baseline, cart, requestId = null) {
      const current = this.cartQuantities(cart, requestId);
      const added = [];
      const remaining = [];
      for (const item of requested) {
        const id = String(item.id);
        const quantity = Math.max(0, Math.min(Number(item.quantity), (current[id] || 0) - (requestId ? 0 : Number(baseline[id]) || 0)));
        if (quantity) added.push({ id, quantity });
        if (quantity < item.quantity) remaining.push({ id, quantity: item.quantity - quantity });
      }
      return { added, remaining };
    }

    applyReconciliation(result) {
      for (const item of result.added) {
        const quantity = Math.max(0, (this.draft.get(item.id) || 0) - item.quantity);
        quantity ? this.draft.set(item.id, quantity) : this.draft.delete(item.id);
      }
      this.pending = null;
      this.persistDraft();
      this.render();
    }

    async recoverPending() {
      if (!this.pending || this.busy || !this.cart) return;
      const result = this.reconcile(this.pending.requested, this.pending.baseline, this.cart, this.pending.requestId);
      if (this.pending.uncertain && result.remaining.length) {
        this.setError(this.string('uncertainCart', 'We cannot confirm your cart yet. Your picks are saved. Check your cart before trying again.'), true);
        return;
      }
      this.applyReconciliation(result);
      this.setError(result.remaining.length ? this.string('recoveredPartial', 'Your cart was checked. Only picks that still need to be added remain here. Review your cart before adding them.') : '');
      await this.applyOffer().catch(() => this.setError(this.string('discountError', 'Check your cart total before checkout.')));
      this.showCartLink();
    }

    async recheckCart() {
      this.setStatus(this.string('checkingCart', 'Checking your cart…'));
      try { await this.refreshCart(); await this.recoverPending(); }
      catch { this.setError(this.string('uncertainCart', 'We cannot confirm your cart yet. Your picks are saved. Check your cart before trying again.'), true); }
    }

    async submit() {
      if (this.busy || this.pending || this.switchingMode || window.__fctPickerCartBusy || !this.draft.size) return;
      if (this.offerPreview && this.summaryValues().tier) return;
      if (!this.catalogComplete) {
        this.setStatus(this.string('catalogLoading', 'Loading all available designs. Please try again in a moment.'));
        return;
      }
      this.validateDraft();
      const requested = Array.from(this.draft, ([id, quantity]) => ({ id, quantity })).filter(item => this.variants.get(item.id)?.available);
      if (!requested.length) return this.render();
      const minimumBundleQuantity = Math.max(0, Math.floor(Number(this.config.offer?.minimumBundleQuantity) || 0));
      const requestedCount = requested.reduce((sum, item) => sum + item.quantity, 0);
      if (requestedCount < minimumBundleQuantity) {
        this.setStatus(this.string('completeBundle', 'Pick {remaining} more to complete your bundle', { remaining: minimumBundleQuantity - requestedCount, quantity: minimumBundleQuantity }));
        return this.render();
      }
      this.busy = true;
      window.__fctPickerCartBusy = true;
      this.closeVariantMenu();
      this.setError('');
      this.setStatus(this.string('adding', 'Adding your picks…'));
      this.renderSummary();
      let mutationError = null;
      let result = null;
      try {
        const before = await this.refreshCart();
        if (before.currency && before.currency !== (this.config.currency || 'USD')) throw new Error(this.string('currencyChanged', 'Your currency changed. Reload this page to refresh prices before adding.'));
        if (await this.refreshSelectedProducts()) {
          this.setError(this.string('priceChanged', 'Review your updated picks before adding.'));
          return;
        }
        const baseline = this.cartQuantities(before);
        const requestId = crypto.randomUUID();
        this.pending = { baseline, requested, requestId, uncertain: true, startedAt: Date.now() };
        this.persistDraft();
        try {
          await this.request(this.cartURL('/add.js'), { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items: requested.map(item => ({ ...item, properties: { _fct_picker_request: requestId } })) }) });
          this.pending.uncertain = false;
          this.persistDraft();
        } catch (error) { mutationError = error; this.pending.uncertain = !error.status || error.status >= 500; this.persistDraft(); }
        let after;
        try { after = await this.refreshCart(); }
        catch {
          this.pending.uncertain = true;
          this.persistDraft();
          this.setError(this.string('uncertainCart', 'We cannot confirm your cart yet. Your picks are saved. Check your cart before trying again.'), true);
          return;
        }
        result = this.reconcile(requested, baseline, after, requestId);
        if (this.pending.uncertain && result.remaining.length) {
          this.setError(this.string('uncertainCart', 'We cannot confirm your cart yet. Your picks are saved. Check your cart before trying again.'), true);
          return;
        }
        this.applyReconciliation(result);
        const count = result.added.reduce((sum, item) => sum + item.quantity, 0);
        if (result.remaining.length) {
          this.setError(mutationError?.message || this.string('partialAdded', 'Some picks could not be added. Your cart is updated, and the remaining picks are still here.'));
        }
        if (count) {
          try { after = await this.applyOffer(); }
          catch { this.setError(this.string('discountError', 'Check your cart total before checkout.')); }
          this.setStatus(this.string('added', '{count} picks added to your cart.', { count }));
          this.closeSummary();
          this.closeDetail();
          document.dispatchEvent(new CustomEvent('fct:picker:added', { detail: { items: result.added, cart: after, source: 'fct-sticker-picker' } }));
          await this.openCart();
        } else if (!mutationError) this.setError(this.string('nothingAdded', 'No picks were added. Please review your cart and try again.'));
      } catch (error) {
        this.setError(error.message || this.string('requestFailed', 'We could not complete that request.'));
      } finally {
        this.busy = false;
        window.__fctPickerCartBusy = false;
        this.renderSummary();
        this.updateSelectionMarkers();
      }
    }

    async refreshSelectedProducts() {
      let changed = false;
      const selected = [...new Set([...this.draft.keys()].map(id => this.variants.get(id)?.product).filter(Boolean))];
      await Promise.all(selected.map(async product => {
        const url = new URL(product.url, location.origin);
        url.pathname = `${url.pathname.replace(/\/$/, '')}.js`;
        url.search = '';
        const fresh = await this.request(url.href);
        for (const variant of product.variants) {
          const current = fresh.variants?.find(item => String(item.id) === variant.id);
          if (!current || current.price !== variant.price || current.available !== variant.available) {
            if (this.draft.has(variant.id)) changed = true;
            variant.available = Boolean(current?.available);
            if (current) variant.price = current.price;
          }
        }
      }));
      this.validateDraft();
      this.render();
      return changed;
    }

    async applyOffer() {
      if (this.config.offer?.enabled !== true || !this.cart) return this.cart;
      const count = this.qualifyingCartCount();
      const qualifyingCodes = new Set(this.tiers.filter(item => item.quantity <= count && item.code).map(item => item.code.toLowerCase()));
      const hasApplicableOffer = cart => (cart.discount_codes || []).some(item => item.applicable === true && qualifyingCodes.has(item.code?.toLowerCase()));
      const retiredCodes = Array.isArray(this.config.offer?.retiredCodes) ? this.config.offer.retiredCodes : [];
      const ownCodes = new Set([...this.tiers.map(item => item.code), ...retiredCodes].filter(code => typeof code === 'string' && code).map(code => code.toLowerCase()));
      const existingCodes = (this.cart.discount_codes || []).map(item => item.code).filter(Boolean);
      const codes = existingCodes.filter(code => !ownCodes.has(code.toLowerCase()));
      if (count) codes.push(...this.tiers.map(item => item.code).filter(Boolean));
      const discountCodes = [...new Map(codes.map(code => [code.toLowerCase(), code])).values()];
      const sameCodes = existingCodes.length === discountCodes.length && existingCodes.every(code => discountCodes.some(item => item.toLowerCase() === code.toLowerCase()));
      const applicable = !qualifyingCodes.size || hasApplicableOffer(this.cart);
      if (sameCodes && applicable) return this.cart;
      const cart = await this.request(this.cartURL('/update.js'), {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ discount: discountCodes.join(',') })
      });
      if (!Array.isArray(cart.items)) throw new Error('Invalid cart response');
      this.cart = cart;
      this.syncCartCount(cart);
      this.renderSummary();
      if (qualifyingCodes.size && !hasApplicableOffer(cart)) {
        throw new Error(this.string('discountError', 'Your bundle discount could not be confirmed. Check your cart total before checkout.'));
      }
      return cart;
    }

    async openCart() {
      this.showCartLink();
      try {
        if (typeof window.monster_refresh === 'function') {
          window.monster_refresh(true);
          return;
        }
      } catch {}
    }

    showCartLink() {
      const status = this.querySelector('[data-picker-status]');
      if (status) {
        const link = document.createElement('a');
        link.href = window.theme?.routes?.cart_url || this.cartURL('');
        link.textContent = this.string('viewCart', 'View cart');
        link.className = 'fct-picker__cart-link';
        status.append(document.createTextNode(' '), link);
      }
    }

    async loadCatalog() {
      if (this.loadingCatalog || this.catalogComplete) return;
      this.loadingCatalog = true;
      this.toggleLoadingControls(true);
      let next = this.nextCatalogURL ?? this.config.nextUrl;
      const visited = this.visitedCatalogURLs ||= new Set();
      try {
        while (next) {
          const url = new URL(next, location.origin);
          if (!url.searchParams.has('section_id') && !url.searchParams.has('sections')) url.searchParams.set('section_id', this.config.sectionId);
          if (url.origin !== location.origin || visited.has(url.href) || visited.size >= 50) throw new Error('Invalid catalog pagination');
          const response = await fetch(url.href, { credentials: 'same-origin', signal: this.controller.signal });
          if (!response.ok) throw new Error('Catalog unavailable');
          let html = await response.text();
          if (response.headers.get('Content-Type')?.includes('application/json')) {
            const sections = JSON.parse(html);
            html = sections[this.config.sectionId];
            if (!html) throw new Error('Catalog section unavailable');
          }
          const documentFragment = new DOMParser().parseFromString(html, 'text/html');
          const productNode = documentFragment.querySelector('[data-picker-products]');
          const nextConfig = this.readJSON(documentFragment.querySelector('[data-picker-config]'), {});
          const products = this.readJSON(productNode, null);
          if (!Array.isArray(products)) throw new Error('Invalid catalog response');
          this.addProducts(products);
          visited.add(url.href);
          next = nextConfig.nextUrl || documentFragment.querySelector('[data-picker-next]')?.getAttribute('href') || '';
          this.nextCatalogURL = next;
          this.renderCatalog();
        }
        this.catalogComplete = true;
        this.validateDraft();
        this.render();
      } catch (error) {
        if (error.name !== 'AbortError') {
          this.nextCatalogURL = next;
          this.setError(this.string('catalogError', 'We could not load all designs. Try loading the catalog again.'));
        }
      } finally {
        this.loadingCatalog = false;
        this.toggleLoadingControls(false);
        this.renderSummary();
      }
    }

    toggleLoadingControls(loading) {
      this.toggleAttribute('data-catalog-loading', loading);
      for (const node of this.querySelectorAll('[data-picker-search], [data-picker-sort]')) node.disabled = !this.catalogComplete;
      const more = this.querySelector('[data-picker-load-more]');
      if (more) { more.hidden = this.catalogComplete; more.disabled = loading; }
      const grid = this.querySelector('[data-picker-grid]');
      grid?.setAttribute('aria-busy', String(loading));
    }

    render() {
      this.renderCatalog();
      this.renderSummary();
    }

    renderCatalog() {
      const grid = this.querySelector('[data-picker-grid]');
      if (!grid) return;
      this.closeVariantMenu();
      const search = (this.querySelector('[data-picker-search]')?.value || '').trim().toLocaleLowerCase();
      const sort = this.querySelector('[data-picker-sort]')?.value || 'featured';
      let products = Array.from(this.products.values()).filter(product => !search || `${product.title} ${product.category} ${product.variants.map(variant => variant.title).join(' ')}`.toLocaleLowerCase().includes(search));
      const price = product => {
        const available = product.variants.filter(variant => variant.available);
        return Math.min(...(available.length ? available : product.variants).map(variant => variant.price));
      };
      if (sort === 'title-asc') products.sort((a, b) => a.title.localeCompare(b.title));
      if (sort === 'price-asc') products.sort((a, b) => price(a) - price(b));
      if (sort === 'price-desc') products.sort((a, b) => price(b) - price(a));
      const existing = new Map(Array.from(grid.querySelectorAll('[data-picker-card]'), card => [String(card.dataset.productId), card]));
      const fragment = document.createDocumentFragment();
      for (const product of products) {
        const card = existing.get(product.id) || this.createCard();
        card.dataset.productId = product.id;
        this.updateCard(card, product);
        fragment.append(card);
      }
      grid.replaceChildren(fragment);
      const empty = this.querySelector('[data-picker-empty]');
      if (empty) empty.hidden = Boolean(products.length) || !this.catalogComplete;
      this.setText('[data-picker-result-count]', this.string('results', '{count} designs', { count: products.length }));
    }

    createCard() {
      const template = this.querySelector('[data-picker-card-template]');
      if (template?.content) {
        const card = template.content.cloneNode(true).querySelector('[data-picker-card]');
        if (card) return card;
      }
      const card = document.createElement('article');
      card.className = 'fct-picker__card';
      card.setAttribute('data-picker-card', '');
      const imageLink = document.createElement('a');
      imageLink.className = 'fct-picker__image-link';
      const image = document.createElement('img');
      image.className = 'fct-picker__image';
      image.setAttribute('data-picker-image', '');
      image.loading = 'lazy';
      image.width = 400;
      image.height = 400;
      imageLink.append(image);
      const body = document.createElement('div');
      body.className = 'fct-picker__card-body';
      const title = document.createElement('a');
      title.className = 'fct-picker__card-title';
      title.setAttribute('data-picker-title', '');
      const price = document.createElement('p');
      price.className = 'fct-picker__card-price';
      price.setAttribute('data-picker-price', '');
      const select = document.createElement('select');
      select.className = 'fct-picker__variant';
      select.setAttribute('data-picker-variant', '');
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'fct-picker__add';
      button.setAttribute('data-picker-add', '');
      body.append(title, price, select, button);
      card.append(imageLink, body);
      return card;
    }

    updateCard(card, product) {
      if (!card || !product) return;
      const selected = this.variants.get(this.selectedVariants.get(product.id));
      const variant = selected?.product.id === product.id ? selected : this.firstVariant(product);
      const select = card.querySelector('[data-picker-variant]');
      if (select && select.dataset.productId !== product.id) {
        select.replaceChildren(...product.variants.map(item => {
          const option = document.createElement('option');
          option.value = item.id;
          option.disabled = !item.available;
          option.textContent = `${item.title === 'Default Title' ? this.string('standardVariant', 'Standard') : item.title} — ${this.money(item.price)}${item.available ? '' : ` · ${this.string('soldOut', 'Sold out')}`}`;
          return option;
        }));
        select.dataset.productId = product.id;
      }
      if (select) {
        let label = card.querySelector('[data-picker-variant-label]');
        if (!label) {
          label = document.createElement('label');
          label.className = 'fct-picker__variant-label';
          label.setAttribute('data-picker-variant-label', '');
          select.parentElement.insertBefore(label, select);
        }
        select.id = `FctPickerVariant-${this.config.sectionId || 'sticker'}-${product.id}`;
        label.htmlFor = select.id;
        label.textContent = this.string('chooseOption', 'Choose an option');
        label.hidden = true;
        select.value = variant.id;
        select.hidden = true;
        select.setAttribute('aria-label', this.string('variantLabel', 'Choose an option for {title}', { title: product.title }));
      }
      const image = card.querySelector('[data-picker-image]');
      if (image) {
        if (variant.image) image.src = variant.image;
        image.alt = product.title;
      }
      const title = card.querySelector('[data-picker-title]');
      if (title) title.textContent = product.title;
      for (const link of card.querySelectorAll('a')) link.href = product.url || '#';
      const price = card.querySelector('[data-picker-price]');
      if (price) price.textContent = this.money(variant.price);
      const category = card.querySelector('[data-picker-category]');
      if (category) category.textContent = product.category;
      const add = card.querySelector('[data-picker-add]');
      if (add) {
        add.type = 'button';
        add.disabled = !variant.available || this.busy || Boolean(this.pending) || this.totalPicks() >= this.maxPicks;
        add.textContent = this.string('pickThis', 'Add sticker');
        add.setAttribute('aria-label', variant.available ? this.string('addLabel', 'Add {title}', { title: product.title }) : this.string('soldOut', 'Sold out'));
      }
      if (product.variants.length > 1) this.ensureVariantPanel(card, product);
      const selectedQuantity = product.variants.reduce((sum, item) => sum + (this.draft.get(item.id) || 0), 0);
      card.toggleAttribute('data-selected', selectedQuantity > 0);
      card.dataset.selectedQuantity = String(selectedQuantity);
      const badge = card.querySelector('[data-picker-card-count]');
      if (badge) { badge.hidden = !selectedQuantity; badge.textContent = String(selectedQuantity); }
    }

    updateSelectionMarkers() {
      for (const card of this.querySelectorAll('[data-picker-grid] [data-picker-card]')) this.updateCard(card, this.products.get(String(card.dataset.productId)));
    }

    ensureVariantPanel(card, product) {
      let panel = card.querySelector('[data-picker-variant-panel]');
      if (!panel) {
        panel = document.createElement('fieldset');
        panel.className = 'fct-picker__variant-panel';
        panel.setAttribute('data-picker-variant-panel', '');
        panel.setAttribute('popover', 'auto');
        panel.hidden = true;
        card.append(panel);
      }
      panel.id = `FctPickerOptions-${this.config.sectionId || 'sticker'}-${product.id}`;
      const add = card.querySelector('[data-picker-add]');
      add?.setAttribute('aria-controls', panel.id);
      add?.setAttribute('aria-expanded', String(this.openVariantCard === card));
      if (panel.dataset.productId !== product.id) {
        const legend = document.createElement('legend');
        legend.className = 'fct-picker__variant-legend';
        legend.textContent = this.string('chooseOption', 'Choose an option');
        panel.replaceChildren(legend, ...product.variants.map(variant => {
          const choice = document.createElement('button');
          choice.type = 'button';
          choice.className = 'fct-picker__variant-choice';
          choice.setAttribute('data-picker-variant-choice', '');
          choice.dataset.variantId = variant.id;
          const title = document.createElement('span');
          title.textContent = `${variant.title === 'Default Title' ? this.string('standardVariant', 'Standard') : variant.title}${variant.available ? '' : ` — ${this.string('soldOut', 'Sold out')}`}`;
          const price = document.createElement('span');
          price.textContent = this.money(variant.price);
          choice.append(title, price);
          return choice;
        }));
        panel.dataset.productId = product.id;
      }
      const selected = this.selectedVariants.get(product.id) || this.firstVariant(product)?.id;
      for (const choice of panel.querySelectorAll('[data-picker-variant-choice]')) {
        choice.disabled = !this.variants.get(choice.dataset.variantId)?.available || this.busy || Boolean(this.pending) || this.totalPicks() >= this.maxPicks;
        choice.dataset.selected = String(choice.dataset.variantId === selected);
      }
      if (panel.pickerController !== this.controller) {
        panel.pickerController = this.controller;
        panel.addEventListener('toggle', event => {
          if (event.newState === 'closed' && this.openVariantCard === card) this.closeVariantMenu();
        }, { signal: this.controller.signal });
      }
      return panel;
    }

    toggleVariantMenu(card) {
      if (this.openVariantCard === card) {
        this.closeVariantMenu(true);
        return;
      }
      const product = this.products.get(String(card.dataset.productId));
      if (!product || product.variants.length < 2) return;
      this.closeVariantMenu();
      this.closeDetail();
      const panel = this.ensureVariantPanel(card, product);
      this.openVariantCard = card;
      card.setAttribute('data-picker-choices-open', '');
      card.querySelector('[data-picker-add]')?.setAttribute('aria-expanded', 'true');
      panel.hidden = false;
      this.positionVariantMenu();
      if (typeof panel.showPopover === 'function') panel.showPopover();
      else panel.setAttribute('data-picker-overlay-open', '');
      const choice = Array.from(panel.querySelectorAll('[data-picker-variant-choice]')).find(option => option.dataset.selected === 'true' && !option.disabled) || panel.querySelector('[data-picker-variant-choice]:not(:disabled)');
      choice?.focus({ preventScroll: true });
    }

    positionVariantMenu() {
      const card = this.openVariantCard;
      if (!card) return;
      const panel = card.querySelector('[data-picker-variant-panel]');
      const bounds = card.querySelector('[data-picker-add]').getBoundingClientRect();
      const viewportHeight = window.visualViewport?.height || window.innerHeight;
      const width = Math.min(bounds.width, window.innerWidth - 16);
      const left = Math.max(8, Math.min(bounds.left, window.innerWidth - width - 8));
      const below = viewportHeight - bounds.bottom - 12;
      const above = bounds.top - 12;
      const useBelow = below >= 92 || below >= above;
      panel.style.width = `${width}px`;
      panel.style.left = `${left}px`;
      panel.style.maxHeight = `${Math.max(46, useBelow ? below : above)}px`;
      panel.style.top = useBelow ? `${bounds.bottom + 4}px` : 'auto';
      panel.style.bottom = useBelow ? 'auto' : `${viewportHeight - bounds.top + 4}px`;
    }

    closeVariantMenu(restoreFocus = false) {
      const card = this.openVariantCard;
      if (!card) return;
      this.openVariantCard = null;
      const panel = card.querySelector('[data-picker-variant-panel]');
      if (typeof panel?.hidePopover === 'function' && panel.matches(':popover-open')) panel.hidePopover();
      if (panel) {
        panel.removeAttribute('data-picker-overlay-open');
        panel.hidden = true;
      }
      card.removeAttribute('data-picker-choices-open');
      const add = card.querySelector('[data-picker-add]');
      add?.setAttribute('aria-expanded', 'false');
      if (restoreFocus) add?.focus({ preventScroll: true });
    }

    qualifyingCartCount() {
      if (!this.cart) return 0;
      return this.cart.items.reduce((sum, item) => this.eligibleVariantIds.has(String(item.variant_id || item.id)) && !item.selling_plan_allocation ? sum + Math.max(0, Number(item.quantity) || 0) : sum, 0);
    }

    summaryValues() {
      const count = this.totalPicks();
      const existing = this.qualifyingCartCount();
      const subtotal = Array.from(this.draft).reduce((sum, [id, quantity]) => sum + (this.variants.get(id)?.price || 0) * quantity, 0);
      const tier = [...this.tiers].reverse().find(item => item.quantity <= count && count <= this.maxPicks);
      const verified = (this.config.discountVerified === true || this.offerPreview) && this.catalogComplete && Boolean(this.cart) && (!this.cart.currency || this.cart.currency === (this.config.currency || 'USD'));
      let savings = 0;
      if (verified && tier) {
        const prices = Array.from(this.draft).flatMap(([id, quantity]) => {
          const variant = this.variants.get(id);
          if (!variant || (this.eligibleVariantIds.size && !this.eligibleVariantIds.has(id))) return [];
          return Array(quantity).fill(variant.price);
        }).sort((a, b) => a - b);
        const discountedQuantity = tier.freeQuantity || tier.discountQuantity || 0;
        const percentage = tier.freeQuantity ? 100 : tier.discountPercentage || 0;
        savings = prices.slice(0, discountedQuantity).reduce((sum, price) => sum + Math.round(price * percentage / 100), 0);
      }
      return { count, existing, subtotal, tier, savings, total: subtotal - savings, verified };
    }

    offerValues(tier) {
      return { quantity: tier.quantity, buyQuantity: tier.quantity - tier.freeQuantity, freeQuantity: tier.freeQuantity, percentage: tier.percentage, price: this.money(tier.total) };
    }

    renderMilestones(values) {
      const rail = this.querySelector('[data-picker-milestones]');
      if (!rail) return;
      const maximum = this.tiers[this.tiers.length - 1]?.quantity || 15;
      const signature = this.tiers.map(tier => `${tier.quantity}/${tier.freeQuantity}/${tier.percentage}`).join(':');
      if (rail.dataset.tiers !== signature) {
        rail.replaceChildren(...this.tiers.map(tier => {
          const milestone = document.createElement('li');
          milestone.className = 'fct-picker__milestone';
          milestone.setAttribute('data-picker-tier-quantity', String(tier.quantity));
          const number = document.createElement('span');
          number.className = 'fct-picker__milestone-number';
          number.setAttribute('aria-hidden', 'true');
          number.textContent = String(tier.quantity);
          const check = document.createElement('span');
          check.className = 'fct-picker__milestone-check';
          check.setAttribute('aria-hidden', 'true');
          check.textContent = '✓';
          number.append(check);
          const label = document.createElement('span');
          label.className = 'fct-picker__milestone-label';
          label.textContent = (this.config.discountVerified || this.offerPreview) ? this.string('offerLabel', tier.freeQuantity ? 'Buy {buyQuantity}, get {freeQuantity} free' : '{quantity} picks · {percentage}% off', this.offerValues(tier)) : this.string('milestonePicks', '{quantity} picks', { quantity: tier.quantity });
          milestone.append(number, label);
          return milestone;
        }));
        rail.dataset.tiers = signature;
      }
      const combined = values.count;
      const next = this.tiers.find(tier => tier.quantity > combined);
      for (const milestone of rail.querySelectorAll('[data-picker-tier-quantity]')) {
        const quantity = Number(milestone.getAttribute('data-picker-tier-quantity'));
        const reached = combined >= quantity;
        const current = next?.quantity === quantity;
        milestone.toggleAttribute('data-reached', reached);
        milestone.toggleAttribute('data-current', current);
        const position = `${quantity / maximum * 100}%`;
        milestone.style.setProperty('--picker-position', position);
        milestone.style.setProperty('--picker-milestone-position', position);
        const tier = this.tiers.find(item => item.quantity === quantity);
        const label = (this.config.discountVerified || this.offerPreview) ? this.string('offerLabel', tier.freeQuantity ? 'Buy {buyQuantity}, get {freeQuantity} free' : '{quantity} picks · {percentage}% off', this.offerValues(tier)) : this.string('milestonePicks', '{quantity} picks', { quantity });
        const state = reached ? this.string('milestoneReached', 'reached') : current ? this.string('milestoneNext', 'next milestone') : this.string('milestonePending', 'not reached yet');
        milestone.setAttribute('aria-label', `${label}, ${state}`);
      }
    }

    renderSummary() {
      if (!this.draft) return;
      this.renderModeButtons();
      const values = this.summaryValues();
      this.renderMilestones(values);
      const container = this.querySelector('[data-picker-summary-items]');
      if (container) {
        const active = this.contains(document.activeElement) ? document.activeElement : null;
        const activeId = active?.dataset.variantId;
        const activeAction = active && ['minus', 'plus', 'remove'].find(action => active.hasAttribute(`data-picker-${action}`));
        const fragment = document.createDocumentFragment();
        for (const [id, quantity] of this.draft) {
          const variant = this.variants.get(id);
          if (!variant) continue;
          const row = document.createElement('li');
          row.className = 'fct-picker__selection';
          const image = document.createElement('img');
          image.className = 'fct-picker__selection-image';
          if (variant.image) image.src = variant.image;
          image.alt = '';
          image.width = 56;
          image.height = 56;
          const copy = document.createElement('div');
          copy.className = 'fct-picker__selection-copy';
          const title = document.createElement('span');
          title.className = 'fct-picker__selection-name';
          title.textContent = variant.product.title;
          const option = document.createElement('span');
          option.className = 'fct-picker__selection-variant';
          option.textContent = `${variant.title === 'Default Title' ? this.string('standardVariant', 'Standard') : variant.title} · ${this.money(variant.price * quantity)}`;
          copy.append(title, option);
          const controls = document.createElement('div');
          controls.className = 'fct-picker__quantity';
          for (const action of ['minus', 'value', 'plus']) {
            const node = document.createElement(action === 'value' ? 'span' : 'button');
            node.className = action === 'value' ? 'fct-picker__quantity-value' : 'fct-picker__quantity-button';
            node.textContent = action === 'value' ? String(quantity) : action === 'minus' ? '−' : '+';
            if (action !== 'value') {
              node.type = 'button';
              node.dataset.variantId = id;
              node.setAttribute(`data-picker-${action}`, '');
              node.disabled = this.busy || Boolean(this.pending) || (action === 'plus' && values.count >= this.maxPicks);
              node.setAttribute('aria-label', this.string(`${action}Label`, `${action === 'minus' ? 'Decrease quantity of' : action === 'plus' ? 'Increase quantity of' : 'Remove'} {title}`, { title: variant.product.title }));
            }
            controls.append(node);
          }
          row.append(image, copy, controls);
          fragment.append(row);
        }
        container.replaceChildren(fragment);
        if (activeAction && activeId) container.querySelector(`[data-picker-${activeAction}][data-variant-id="${activeId}"]`)?.focus({ preventScroll: true });
      }
      this.setText('[data-picker-count]', String(values.count));
      this.setText('[data-picker-cart-count]', String(values.existing));
      this.setText('[data-picker-subtotal]', this.money(values.subtotal));
      this.setText('[data-picker-total]', this.money(values.total));
      this.setText('[data-picker-savings]', this.money(values.savings));
      for (const node of this.querySelectorAll('[data-picker-savings-row], [data-picker-compare]')) node.hidden = !values.verified || !values.savings;
      for (const node of this.querySelectorAll('[data-picker-cart-count-row]')) node.hidden = !values.existing;
      const next = this.tiers.find(tier => tier.quantity > values.count);
      const target = this.tiers[this.tiers.length - 1]?.quantity || 15;
      for (const progress of this.querySelectorAll('[data-picker-progress]')) {
        const value = Math.min(target, values.count);
        if (progress.tagName === 'PROGRESS') { progress.max = target; progress.value = value; }
        else { progress.setAttribute('aria-valuemin', '0'); progress.setAttribute('aria-valuemax', String(target)); progress.setAttribute('aria-valuenow', String(value)); progress.style.setProperty('--picker-progress', `${value / target * 100}%`); }
      }
      const message = next ? this.string('nextTier', next.freeQuantity ? '{remaining} more to unlock {freeQuantity} free stickers.' : '{remaining} more to reach {quantity} picks.', { remaining: next.quantity - values.count, ...this.offerValues(next) }) : this.string('topTier', 'Buy {buyQuantity}, get {freeQuantity} free — your picks are ready.', values.tier ? this.offerValues(values.tier) : {});
      this.setText('[data-picker-progress-copy]', (this.config.discountVerified || this.offerPreview) ? message : next ? this.string('plainNext', '{remaining} more to reach {quantity} picks.', {remaining: next.quantity - values.count, quantity: next.quantity}) : this.string('plainTop', 'Your picks are ready. Add them to your cart.'));
      if (this.config.strings.offerHeading) this.setText('[data-picker-offer]', this.string('offerHeading', 'Pick your sticker bundle.'));
      this.querySelectorAll('[data-picker-offer]').forEach(node => { node.hidden = !this.config.discountVerified && !this.offerPreview; });
      this.querySelectorAll('[data-picker-offer-preview]').forEach(node => { node.hidden = !this.offerPreview; });
      for (const milestone of this.querySelectorAll('[data-picker-tier-quantity]')) milestone.toggleAttribute('data-reached', values.count >= Number(milestone.dataset.pickerTierQuantity));
      const minimumBundleQuantity = Math.max(0, Math.floor(Number(this.config.offer?.minimumBundleQuantity) || 0));
      const incompleteBundle = values.count < minimumBundleQuantity;
      for (const submit of this.querySelectorAll('[data-picker-submit]')) {
        submit.disabled = !values.count || incompleteBundle || this.busy || Boolean(this.pending) || !this.catalogComplete || (this.offerPreview && Boolean(values.tier));
        submit.setAttribute('aria-busy', String(this.busy));
        submit.textContent = this.offerPreview && values.count && values.tier ? this.string('offerPending', 'Bundle checkout pending') : this.busy ? this.string('adding', 'Adding your picks…') : incompleteBundle ? this.string('completeBundle', 'Pick {remaining} more to complete your bundle', { remaining: minimumBundleQuantity - values.count, quantity: minimumBundleQuantity }) : this.string('submit', 'Add my picks');
      }
      const clear = this.querySelector('[data-picker-clear]');
      if (clear) { clear.disabled = !values.count || this.busy || Boolean(this.pending); clear.hidden = !values.count; }
      this.toggleAttribute('data-empty-draft', !values.count);
      this.setText('[data-picker-summary-empty]', this.string('emptyPicks', 'Your favorites will appear here. Pick a design to get started.'));
      for (const node of this.querySelectorAll('[data-picker-summary-empty]')) node.hidden = values.count > 0;
      this.observePicksHeight(container);
      this.schedulePicksHeight();
    }

    observePicksHeight(container) {
      if (!this.picksResizeObserver) return;
      const nodes = new Set(container ? [container, ...Array.from(container.children).slice(0, 4)] : []);
      for (const node of this.observedPickNodes) if (!nodes.has(node)) this.picksResizeObserver.unobserve(node);
      for (const node of nodes) if (!this.observedPickNodes.has(node)) this.picksResizeObserver.observe(node);
      this.observedPickNodes = nodes;
    }

    schedulePicksHeight() {
      if (!this.isConnected || this.controller?.signal.aborted || this.picksHeightFrame != null) return;
      this.picksHeightFrame = requestAnimationFrame(() => {
        this.picksHeightFrame = null;
        if (!this.isConnected || this.controller?.signal.aborted) return;
        this.updateSummaryHeight();
        const container = this.querySelector('[data-picker-summary-items]');
        if (!container) return;
        if (!container.children.length && this.summary) this.summary.scrollTop = 0;
      });
    }

    updateSummaryHeight() {
      if (!this.summary || !this.mount) return;
      if (this.mobileQuery.matches || this.summary.parentElement !== this.mount) {
        this.summary.style.removeProperty('--picker-summary-max-height');
        return;
      }
      const viewport = window.visualViewport;
      const viewportTop = viewport?.offsetTop || 0;
      const viewportHeight = viewport?.height || window.innerHeight;
      const top = Math.max(viewportTop, this.mount.getBoundingClientRect().top);
      const available = viewportTop + viewportHeight - top - 20;
      if (available <= 0) {
        this.summary.style.removeProperty('--picker-summary-max-height');
        return;
      }
      const header = this.summary.querySelector('.fct-picker__summary-header');
      const button = this.summary.querySelector('[data-picker-submit]');
      const styles = getComputedStyle(this.summary);
      const paddingTop = parseFloat(styles.paddingTop) || 0;
      const paddingBottom = parseFloat(styles.paddingBottom) || 0;
      const borderHeight = (parseFloat(styles.borderTopWidth) || 0) + (parseFloat(styles.borderBottomWidth) || 0);
      const headerHeight = header?.getBoundingClientRect().height || 0;
      const buttonHeight = button?.getBoundingClientRect().height || 0;
      const protectedHeight = headerHeight + buttonHeight + paddingTop + paddingBottom + borderHeight + 8;
      const offset = `${Math.ceil(headerHeight + paddingTop + 8)}px`;
      if (this.summary.style.getPropertyValue('--picker-summary-header-offset') !== offset) this.summary.style.setProperty('--picker-summary-header-offset', offset);
      const value = `${Math.ceil(Math.max(available, protectedHeight))}px`;
      if (this.summary.style.getPropertyValue('--picker-summary-max-height') !== value) this.summary.style.setProperty('--picker-summary-max-height', value);
    }

    setText(selector, value) { this.querySelectorAll(selector).forEach(node => { node.textContent = value; }); }

    setStatus(message) { this.setText('[data-picker-status]', message); }

    setError(message, recheck = false) {
      const error = this.querySelector('[data-picker-error]');
      if (!error) return;
      error.textContent = message;
      error.hidden = !message;
      if (recheck) {
        const button = document.createElement('button');
        button.type = 'button';
        button.setAttribute('data-picker-recheck', '');
        button.textContent = this.string('recheckCart', 'Recheck cart');
        error.append(document.createTextNode(' '), button);
      }
    }

    openSummary() {
      const content = this.querySelector('[data-picker-dialog-content]');
      if (!this.mobileQuery.matches || !this.dialog || !this.summary || !content) return;
      this.closeDetail();
      this.closeVariantMenu();
      content.append(this.summary);
      if (!this.dialog.open) this.dialog.showModal();
      this.schedulePicksHeight();
    }

    closeSummary() {
      if (this.dialog?.open) this.dialog.close();
      this.restoreSummary();
    }

    restoreSummary() {
      if (this.mount && this.summary && this.summary.parentElement !== this.mount) this.mount.append(this.summary);
      this.schedulePicksHeight();
    }

    ensureDetailDialog() {
      if (this.detailDialog) return this.detailDialog;
      const dialog = document.createElement('dialog');
      dialog.className = 'fct-picker__detail';
      dialog.setAttribute('data-picker-detail', '');
      const content = document.createElement('div');
      content.className = 'fct-picker__detail-content';
      const close = document.createElement('button');
      close.type = 'button';
      close.className = 'fct-picker__detail-close';
      close.setAttribute('data-picker-detail-close', '');
      close.setAttribute('aria-label', this.string('closeDetails', 'Close sticker details'));
      close.textContent = '×';
      const image = document.createElement('img');
      image.className = 'fct-picker__detail-image';
      image.setAttribute('data-picker-detail-image', '');
      image.width = 640;
      image.height = 640;
      const copy = document.createElement('div');
      copy.className = 'fct-picker__detail-copy';
      const title = document.createElement('h2');
      title.className = 'fct-picker__detail-title';
      title.id = `FctPickerDetail-${this.config.sectionId || 'sticker'}`;
      title.setAttribute('data-picker-detail-title', '');
      dialog.setAttribute('aria-labelledby', title.id);
      const price = document.createElement('p');
      price.className = 'fct-picker__detail-price';
      price.setAttribute('data-picker-detail-price', '');
      const select = document.createElement('select');
      select.className = 'fct-picker__variant';
      select.setAttribute('data-picker-detail-variant', '');
      select.id = `${title.id}-variant`;
      const label = document.createElement('label');
      label.className = 'fct-picker__variant-label';
      label.setAttribute('data-picker-detail-variant-label', '');
      label.htmlFor = select.id;
      label.textContent = this.string('chooseOption', 'Choose an option');
      const add = document.createElement('button');
      add.type = 'button';
      add.className = 'fct-picker__add';
      add.setAttribute('data-picker-detail-add', '');
      const status = document.createElement('p');
      status.className = 'fct-picker__detail-status';
      status.setAttribute('data-picker-detail-status', '');
      status.setAttribute('role', 'status');
      status.setAttribute('aria-live', 'polite');
      copy.append(title, price, label, select, add, status);
      content.append(close, image, copy);
      dialog.append(content);
      this.append(dialog);
      this.detailDialog = dialog;
      this.bindDetailDialog(dialog);
      return dialog;
    }

    bindDetailDialog(dialog) {
      if (this.detailDialogController === this.controller) return;
      this.detailDialogController = this.controller;
      const signal = this.controller.signal;
      dialog.addEventListener('close', () => {
        if (!this.dialog?.open && !this.busy && this.detailTrigger?.isConnected) this.detailTrigger.focus({ preventScroll: true });
      }, { signal });
      dialog.addEventListener('click', event => {
        if (event.target !== dialog) return;
        const box = dialog.getBoundingClientRect();
        if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) this.closeDetail();
      }, { signal });
    }

    openDetail(productId, trigger) {
      const product = this.products.get(String(productId || ''));
      if (!product) return;
      this.closeSummary();
      this.closeVariantMenu();
      this.detailProductId = product.id;
      this.detailTrigger = trigger;
      const dialog = this.ensureDetailDialog();
      this.updateDetail();
      if (!dialog.open) dialog.showModal();
    }

    closeDetail() {
      if (this.detailDialog?.open) this.detailDialog.close();
    }

    updateDetail() {
      const product = this.products.get(this.detailProductId);
      const dialog = this.detailDialog;
      if (!product || !dialog) return;
      const selected = this.variants.get(this.selectedVariants.get(product.id));
      const variant = selected?.product.id === product.id ? selected : this.firstVariant(product);
      const select = dialog.querySelector('[data-picker-detail-variant]');
      if (select.dataset.productId !== product.id) {
        select.replaceChildren(...product.variants.map(item => {
          const option = document.createElement('option');
          option.value = item.id;
          option.disabled = !item.available;
          option.textContent = `${item.title === 'Default Title' ? this.string('standardVariant', 'Standard') : item.title} — ${this.money(item.price)}${item.available ? '' : ` · ${this.string('soldOut', 'Sold out')}`}`;
          return option;
        }));
        select.dataset.productId = product.id;
      }
      select.value = variant.id;
      select.hidden = product.variants.length === 1;
      dialog.querySelector('[data-picker-detail-variant-label]').hidden = product.variants.length === 1;
      select.setAttribute('aria-label', this.string('variantLabel', 'Choose an option for {title}', { title: product.title }));
      const image = dialog.querySelector('[data-picker-detail-image]');
      if (variant.image) image.src = variant.image;
      else image.removeAttribute('src');
      image.alt = product.title;
      dialog.querySelector('[data-picker-detail-title]').textContent = product.title;
      dialog.querySelector('[data-picker-detail-price]').textContent = this.money(variant.price);
      dialog.querySelector('[data-picker-detail-status]').textContent = this.string('detailPicked', '{count} of this design selected.', { count: this.draft.get(variant.id) || 0 });
      const add = dialog.querySelector('[data-picker-detail-add]');
      add.disabled = !variant.available || this.busy || Boolean(this.pending) || this.totalPicks() >= this.maxPicks;
      add.textContent = variant.available ? this.string('pickThis', 'Add sticker') : this.string('soldOut', 'Sold out');
      add.setAttribute('aria-label', this.string('addLabel', 'Add {title}', { title: product.title }));
    }
  }

  customElements.define('fct-sticker-picker', FctStickerPicker);
})();



